import * as Sentry from "@sentry/react";

let sentryInitialized = false;

export function initializeSentry(options: { dsn?: string; release?: string; environment?: string; enabled?: boolean }) {
  if (sentryInitialized || typeof window === "undefined") {
    return;
  }

  const dsn = options.dsn?.trim();
  if (!dsn) {
    return;
  }

  sentryInitialized = true;

  Sentry.init({
    dsn,
    environment: options.environment,
    release: options.release?.trim() || undefined,
    enabled: options.enabled ?? true,
    sendDefaultPii: false,
  });
}

export function setSentryUser(user: { id: string; email?: string | null; username?: string | null } | null) {
  if (!sentryInitialized) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
    username: user.username || undefined,
  });
}

export function clearSentryUser() {
  setSentryUser(null);
}

export function captureSentryException(error: unknown) {
  if (!sentryInitialized) {
    return null;
  }

  return Sentry.captureException(error);
}

export function captureSentryMessage(
  message: string,
  options?: {
    level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    fingerprint?: string[];
  },
) {
  if (!sentryInitialized) {
    return null;
  }

  let eventId: string | null = null;
  Sentry.withScope((scope) => {
    if (options?.level) {
      scope.setLevel(options.level);
    }

    if (options?.tags) {
      Object.entries(options.tags).forEach(([key, value]) => scope.setTag(key, value));
    }

    if (options?.extra) {
      Object.entries(options.extra).forEach(([key, value]) => scope.setExtra(key, value));
    }

    if (options?.fingerprint) {
      scope.setFingerprint(options.fingerprint);
    }

    eventId = Sentry.captureMessage(message);
  });

  return eventId;
}
